import { redirect } from '@tanstack/react-router';
import {
  isPermissionDrivenRole,
  type PermissionGroupId,
  type ProfilePermissions,
} from '@strength/domain';
import type { AuthData } from '../auth';
import { planRedirectTarget } from '../route-redirect-targets';
import {
  tabAccessDecision,
  viewAccessDecision,
  type PermissionTab,
} from './permissions-access';

/**
 * Structural slice of the router context the guards need. Defined here —
 * not imported from router.tsx — so this module stays dependency-light and
 * unit-testable without pulling in the router's page imports.
 */
export interface GuardContext {
  auth: AuthData | null | undefined;
  ensurePermissions: () => Promise<ProfilePermissions | null | undefined>;
}

export interface GuardParams {
  teamId: string;
}

/**
 * beforeLoad guard for routes gated by a single View permission key
 * (SC-4788, mirroring the tablet guard): legacy roles always pass — no
 * query, no document; permission-driven roles read myPermissions (through
 * the shared query cache via context.ensurePermissions) and are redirected
 * to the app index when the key is revoked. Fails closed: a failed
 * permissions fetch redirects, so a fetch error can never read as a granted
 * document.
 */
export function viewGuard(group: PermissionGroupId) {
  return async ({ context }: { context: GuardContext }) => {
    const auth = context.auth;
    // Legacy roles (including the tablet-only kiosk) always pass.
    if (auth?.type !== 'org' || !isPermissionDrivenRole(auth.role)) return;
    const decision = await viewAccessDecision(
      auth.role,
      group,
      context.ensurePermissions,
    );
    if (decision === 'redirect') {
      // replace: a pushed entry would let Back re-land on the denied URL,
      // which redirects again — a bounce loop (CodeRabbit on !3124).
      throw redirect({ to: '/', replace: true });
    }
  };
}

/**
 * beforeLoad guard for one tab of a tabbed area — Teams and Library
 * (SC-4792): runs tabAccessDecision against the area's tab list — the SAME
 * source of truth the nav links render from — and redirects before the page
 * mounts. Reads permissions through context.ensurePermissions so tab
 * navigation stays on the query cache.
 *
 * tabTo is typed as T['to'] so the guard wiring cannot drift from the tab
 * list: a typo'd tab path is a compile error instead of a redirect loop
 * that only reproduces for permission-driven roles (the SC-4791 profile).
 */
export function tabGuard<T extends PermissionTab>(
  tabs: readonly T[],
  tabTo: T['to'],
) {
  return async ({
    context,
    params,
  }: {
    context: GuardContext;
    params: GuardParams;
  }) => {
    const auth = context.auth;
    // Legacy roles always pass — no query, no document.
    if (auth?.type !== 'org' || !isPermissionDrivenRole(auth.role)) return;
    const decision = await tabAccessDecision(
      auth.role,
      tabTo,
      tabs,
      context.ensurePermissions,
    );
    if (decision === 'allow') return;
    // replace: same no-bounce-loop reasoning as viewGuard.
    if (decision.kind === 'plan') {
      throw redirect({ ...planRedirectTarget(params.teamId), replace: true });
    }
    // decision.to comes from the tab lists, whose paths are real routes.
    throw redirect({
      to: decision.to,
      params: { teamId: params.teamId },
      replace: true,
    } as Parameters<typeof redirect>[0]);
  };
}
